'use client'

import { useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, Play, RotateCcw } from 'lucide-react'
import { GameLevel } from '../../index'
import { useGameEngine } from '../../utils/useGameEngine'
import { CharacterRenderer, GameUIRenderer, drawCollectEffect } from '../../utils/characterRenderer'
import { VeniceBackgroundRenderer, drawVeniceObstacle, drawSpritz } from './veniceRenderer'

interface VeniceLevelProps {
level: GameLevel
onComplete: (levelId: number, score: number) => void
onBack: () => void
}

const VeniceLevel = ({ level, onComplete, onBack }: VeniceLevelProps) => {
const canvasRef = useRef<HTMLCanvasElement>(null)
const backgroundRenderer = useRef(new VeniceBackgroundRenderer())

// Détection du device (mobile ou desktop)
const isMobile = typeof window !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent)

// Configuration harmonisée pour Venise
const gameConfig = {
  playerSpeed: isMobile ? 1.2 : 1.2, // Julia va plus vite
  targetSpeed: 1.3, // Ben va un peu moins vite
  accelerationMultiplier: isMobile ? 2.5 : 2.5, // boost plus fort
  collectiblePoints: 100,
  victoryBonus: 100,
  jumpHeight: isMobile ? 150 : 180, // saut raisonnable
  jumpSpeed: isMobile ? 4 : 6, // saut plus dynamique
  collectibleSpawnRate: 0.08, // Apparition régulière de spritz
  obstacleSpawnRate: 2.5 // Beaucoup plus de gondoles
  // winDistance reste par défaut (50)
}

// Utiliser le hook de moteur de jeu - Julia court après Ben !
const {
	gameStarted,
	gameOver,
	gameWon,
	score,
	gameState,
	startGame,
	jump,
	isRunning,
	updateCanvasWidth,
	setDrawCallback
} = useGameEngine({
	config: gameConfig,
	playerStartX: 10, // Julia commence à gauche
	targetStartX: 200,  // Ben commence à droite
	accelerationKey: 'ArrowRight', // Julia accélère vers la droite pour rattraper Ben
	jumpKey: 'Space',
	onGameWon: (finalScore) => {
	setTimeout(() => {
		onComplete(level.id, finalScore)
	}, 2000)
	}
})

// Fonction de dessin spécialisée pour Venise
const draw = useCallback(() => {
	const canvas = canvasRef.current
	if (!canvas) return

	const ctx = canvas.getContext('2d')
	if (!ctx) return

	// Sauvegarder le contexte pour la transformation de caméra
	ctx.save()

	// Appliquer la transformation de caméra si nécessaire
	if (gameState.camera.shouldFollow) {
	ctx.translate(-gameState.camera.x, 0)
	}

	// Dessiner l'arrière-plan vénitien
	backgroundRenderer.current.drawBackground(ctx,
	gameState.camera.shouldFollow ? gameState.camera.x : 0,
	canvas.width, canvas.height)

	// Dessiner le sol vénitien
	backgroundRenderer.current.drawGround(ctx,
	gameState.camera.shouldFollow ? gameState.camera.x : 0,
	canvas.width, canvas.height)

	// Écrans de jeu
	if (!gameStarted && !gameOver && !gameWon) {
	ctx.restore() // Restaurer avant de dessiner l'écran de démarrage
	GameUIRenderer.drawStartScreen(ctx, canvas.width, canvas.height,
		'🚤 VENICE RUN 🚤',
		'Julia rattrape Ben à Venise !',
		['➡️ Flèche droite pour accélérer', 'ESPACE pour sauter']
	)
	return
	}

	if (gameWon) {
	GameUIRenderer.drawVictoryScreen(ctx, gameState.camera.x, gameState.camera.shouldFollow,
		canvas.width, canvas.height, gameState.score,
		'🎉 BELLISSIMO ! 🎉',
		'Julia a rattrapé Ben !',
		'💕 Amore à Venezia ! 💕'
	)
	if (!gameState.camera.shouldFollow) ctx.restore()
	return
	}

	if (gameOver) {
	GameUIRenderer.drawGameOverScreen(ctx, gameState.camera.x, gameState.camera.shouldFollow,
		canvas.width, canvas.height, gameState.score)
	if (!gameState.camera.shouldFollow) ctx.restore()
	return
	}

	// Dessiner les personnages - Julia (player) rattrape Ben (target)
	if (gameState.hugAnimation.active) {
	// Pour le câlin, positions logiques
	CharacterRenderer.drawHuggingCouple(ctx,
		gameState.player.x, gameState.player.y - gameState.player.jumpHeight, // Julia (player)
		gameState.target.x, gameState.target.y - gameState.target.jumpHeight, // Ben (target)
		gameState.hugAnimation.time)
	} else {
	// Dessiner Julia (player) avec animation de course et bras tendus - elle poursuit Ben !
	CharacterRenderer.drawJulia(ctx,
		gameState.player.x,
		gameState.player.y - gameState.player.jumpHeight,
		isRunning, // Julia court quand le joueur accélère
		gameState.runningTime)

	// Dessiner Ben (target) - il fuit Julia (sans tendre les bras)
	CharacterRenderer.drawBen(ctx,
		gameState.target.x,
		gameState.target.y - gameState.target.jumpHeight,
		true, // Ben court pour fuir Julia !
		gameState.runningTime,
		false) // Ben ne tend pas les bras car il fuit
	}

	// Dessiner les obstacles (gondoliers énervés)
	gameState.obstacles.forEach(obs => {
	drawVeniceObstacle(ctx, obs.x, obs.y, obs.width, obs.height)
	})

	// Dessiner les collectibles (spritz vénitiens)
	gameState.collectibles.forEach(collectible => {
	if (!collectible.collected) {
		drawSpritz(ctx, collectible.x, collectible.y)
	}
	})

	// Dessiner les pièces (coins)
	gameState.coins?.forEach(coin => {
		if (!coin.collected) {
			ctx.save()
			ctx.beginPath()
			ctx.arc(coin.x + 10, coin.y + 10, 10, 0, 2 * Math.PI)
			ctx.fillStyle = '#FFD700'
			ctx.shadowColor = '#F9E79F'
			ctx.shadowBlur = 8
			ctx.fill()
			ctx.restore()
		}
	})

	// Dessiner les effets visuels de collecte
	gameState.collectEffects.forEach(effect => {
	const isHugEffect = gameState.hugAnimation.active && gameState.hugAnimation.heartsSpawned
	drawCollectEffect(ctx, effect.x, effect.y, effect.opacity, effect.scale, isHugEffect)
	})

	// Restaurer le contexte pour l'interface (fixe à l'écran)
	ctx.restore()

// Interface utilisateur adaptée pour Venise
ctx.fillStyle = '#000'
ctx.font = 'bold 20px Arial'
ctx.textAlign = 'left'
ctx.fillText(`Score: ${gameState.score}`, 10, 30)
ctx.fillText(`Distance: ${Math.round(gameState.distance)}m`, 10, 60)

}, [gameStarted, gameOver, gameWon, gameState, isRunning])

// Setup initial du canvas
useEffect(() => {
	const canvas = canvasRef.current
	if (!canvas) return

	canvas.width = 800
	canvas.height = 400
	updateCanvasWidth(canvas.width)
}, [updateCanvasWidth])

// Enregistrer la fonction de dessin pour le rendu automatique
useEffect(() => {
	setDrawCallback(draw)
}, [draw, setDrawCallback])

// Gestionnaire de clic pour le saut et le démarrage du jeu
const handleCanvasClick = () => {
	if (!gameStarted && !gameOver && !gameWon) {
	startGame()
	} else if (gameStarted && !gameOver && !gameWon) {
	jump()
	} else if (gameOver || gameWon) {
	startGame()
	}
}

return (
	<motion.div
	initial={{ opacity: 0, y: 20 }}
	animate={{ opacity: 1, y: 0 }}
	transition={{ duration: 0.6 }}
	className="space-y-6"
	>
	{/* Header */}
	<div className="flex flex-col items-center w-full mt-2 mb-6">
		<h2 className="text-4xl font-serif font-bold text-bordeaux-700 max-w-lg mx-auto flex items-center justify-center gap-2">
			<span className="text-xl"></span>
			Niveau 2
		</h2>
		<div className="mt-3 text-center text-xl text-bordeaux-700 max-w-md mx-auto">
			<span className="font-medium">Aide Julia à partager ses passions avec Ben !</span>
		</div>
</div>
	{/* Zone de jeu harmonisée, boutons intégrés dans le canvas */}
	<div className="card bg-gradient-to-br from-orange-100 via-yellow-100 to-blue-100 border-2 border-orange-200 rounded-xl shadow-lg relative flex flex-col items-center justify-center" style={{ maxWidth: '820px', margin: '0 auto' }}>
		<div className="relative w-full flex flex-col items-center">
		<canvas
			ref={canvasRef}
			className="w-full border-2 border-yellow-200 rounded-lg bg-gradient-to-b from-orange-100 to-blue-200 cursor-pointer shadow-md"
			style={{ maxWidth: '800px', height: '400px' }}
			onClick={handleCanvasClick}
		/>
		{/* Bouton commencer centré sur le canvas au début */}
		{!gameStarted && !gameOver && !gameWon && (
			<>
			<span className="absolute left-1/2 top-[10%] transform -translate-x-1/2 text-sm text-bordeaux-700 font-medium" style={{ zIndex: 10 }}>
				<p>Comment jouer </p>
			</span>
			<button
			onClick={startGame}
			className="btn-primary flex items-center gap-2 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-lg text-lg px-6 py-3"
			style={{ zIndex: 10 }}
			>
			<Play className="w-5 h-5" />
			Commencer la course
			</button>
			<span className="absolute left-1/2 top-[78%] transform -translate-x-1/2 text-sm text-bordeaux-700 font-medium" style={{ zIndex: 10 }}>
				Attention aux gondoles, on est à Venise !
			</span>
			</>
		)}
		{/* Bouton Rejouer uniquement si gameOver */}
		{gameOver && !gameWon && (
			<button
			onClick={startGame}
			className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/80 border-2 border-orange-300 rounded-lg px-6 py-3 flex items-center gap-2 shadow-lg text-lg font-semibold text-orange-700 hover:bg-orange-100 transition-all"
			>
			<RotateCcw className="w-5 h-5" />
			Rejouer
			</button>
		)}
		{/* Message victoire */}
		{gameWon && (
			<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-green-600 font-semibold text-xl bg-white/80 rounded-lg px-8 py-6 shadow-lg">
			Passage au niveau suivant...
			</div>
		)}
		</div>
	</div>

	</motion.div>
)
}

export default VeniceLevel
